import type { ReactNode } from 'react';
import { useMemo } from 'react';
import type { useSearchFilters } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import { DirectoryPillBar } from '~/ui-rocco/pages/directory-browse/DirectoryPillBar';
import type { CategoryPillItem } from '~/ui-rocco/pages/category-page/components/CategoryPills';
import type { RevampCategoryChip } from '../type';

interface EditorialFilterBarProps {
    /** Every category, in nav order — the same list on every page. */
    items: CategoryPillItem[];
    /** The pill that is on: the category being browsed, or the applied theme. */
    active?: Pick<RevampCategoryChip, 'id'>;
    /**
     * Leads the rail with the directory's own pill. Off on a theme page, whose
     * applied theme is the way back to the whole directory.
     */
    showRootPill?: boolean;
    searchFilters: ReturnType<typeof useSearchFilters>;
    /** The bar's margin, where the page's listing sits on its own measure. */
    gutterClassName?: string;
    /** The sticky band's fill, where the page is on a ground of its own. */
    bandClassName?: string;
    /** The pills that are not the active one, where the page sets its own. */
    inactivePillClassName?: string;
    /** The rule under the bar, where the page draws it in its own ink. */
    ruleClassName?: string;
    /** Off at the directory root, where the trail only names the page itself. */
    showSectionHeading?: boolean;
    /** Set on the right of the pill row. */
    trailing?: ReactNode;
    className?: string;
}

const ARTICLES_ROOT = '/articles';

/**
 * The directory's own pill, at the head of the rail on every editorial page.
 *
 * A pill like the others rather than the bar's back control: the back control
 * carries a chevron and its own width, so a rail that had it on the theme
 * pages and not on the directory nudged every pill along on a tab click. Its
 * id sits well clear of anything the nav tree or a sub-category could carry.
 */
const ROOT_CHIP: RevampCategoryChip = {
    id: -1000,
    title: 'All Articles',
    link: ARTICLES_ROOT,
};

/**
 * The category rail and refine control beneath the masthead.
 *
 * This is the shared `DirectoryPillBar` the products, projects and
 * professionals directories use, so editorial inherits the same refine panel,
 * applied-filter chips, drag-scroll rail and active-pill behaviour rather than
 * a lookalike, keyword field included: a term committed there lands on the
 * directory root as `?search=`, which lists the matches from the site's own
 * article index. One thing differs: no sort options are offered — the
 * listing orders by publish date and nothing on the page can change that.
 */
export const EditorialFilterBar = ({
    items,
    active,
    showRootPill = true,
    searchFilters,
    gutterClassName,
    bandClassName,
    inactivePillClassName,
    ruleClassName,
    showSectionHeading,
    trailing,
    className,
}: EditorialFilterBarProps) => {
    // One crumb only: a second would make the bar draw its back control at
    // the head of the rail, which is the very thing the root pill replaces.
    const breadcrumbs = useMemo(
        () => [{ href: ARTICLES_ROOT, label: 'Articles' }],
        [],
    );
    const pills = useMemo(
        () => (showRootPill ? [ROOT_CHIP, ...items] : items),
        [showRootPill, items],
    );
    // Said outright rather than left to the path: the root's link is a prefix
    // of every category's, so by path it would light on every page.
    const activePillId = active?.id ?? ROOT_CHIP.id;

    return (
        <div className={className}>
            <DirectoryPillBar
                breadcrumbs={breadcrumbs}
                searchFilters={searchFilters}
                sortOptions={[]}
                categoryPills={pills}
                activePillId={activePillId}
                resultName="Article"
                searchPlaceholder="Search Articles..."
                {...(gutterClassName && { gutterClassName })}
                {...(bandClassName && { bandClassName })}
                {...(inactivePillClassName && { inactivePillClassName })}
                {...(ruleClassName && { ruleClassName })}
                {...(showSectionHeading !== undefined && {
                    showSectionHeading,
                })}
                {...(trailing && { trailing })}
            />
        </div>
    );
};
